/**
 * BackgroundRequestPausedError — a BACKGROUND request the session layer declined to send, or declined
 * to refresh for (UNREACHABLE-MODULE-BURNDOWN-001 unit D2, owner ruling D-1 "an idle admin
 * re-authenticates", coordinator ruling on D2-Q1: background polling is not activity).
 *
 * A request marked `{ background: true }` (a poll tick, never a user action) must not keep a session
 * alive. When its access token has expired and nobody has interacted with the page since that token
 * was first used, `createApiClient` does NOT refresh: it rejects the request with this error and
 * pauses every later background request that would present the same token, without a network
 * round-trip. The pause ends when the token changes (a user-initiated request refreshed it) or when
 * the user interacts with the page again.
 *
 * It is not a failure the user needs to see. A poller treats it as "skip this tick" — see
 * `@puffin-core/page-shell`'s `useResource`, which restores the pre-tick state.
 *
 * Properties:
 * - `name`     — always `'BackgroundRequestPausedError'`
 * - `code`     — always `'BACKGROUND_REQUEST_PAUSED'`
 * - `endpoint` — request URL path
 * - `method`   — HTTP method, lowercased
 * - `reason`   — `'idle'` (this request's 401 found no user activity since the token was first
 *   used) or `'paused'` (an earlier background request already paused this token)
 * - `status`   — explicitly `undefined` (no server answer is being reported)
 *
 * @module @puffin-core/api-client/errors/BackgroundRequestPausedError
 */

/** Why a background request was declined. */
export const BACKGROUND_PAUSE_REASONS = Object.freeze({
  IDLE: 'idle',
  PAUSED: 'paused',
});

const REASONS = new Set(Object.values(BACKGROUND_PAUSE_REASONS));

export class BackgroundRequestPausedError extends Error {
  /**
   * @param {Object} opts
   * @param {string} opts.endpoint - Request URL path (non-empty)
   * @param {string} opts.method - HTTP method (non-empty)
   * @param {'idle'|'paused'} opts.reason - Why the request was declined
   */
  constructor(opts) {
    if (!opts || typeof opts !== 'object') {
      throw new TypeError('BackgroundRequestPausedError: constructor opts are required');
    }
    const { endpoint, method, reason } = opts;
    if (typeof endpoint !== 'string' || endpoint.length === 0) {
      throw new TypeError('BackgroundRequestPausedError: opts.endpoint is required');
    }
    if (typeof method !== 'string' || method.length === 0) {
      throw new TypeError('BackgroundRequestPausedError: opts.method is required');
    }
    if (!REASONS.has(reason)) {
      throw new TypeError(
        `BackgroundRequestPausedError: opts.reason must be one of ${[...REASONS].join(', ')}`,
      );
    }
    const normalizedMethod = method.toLowerCase();
    super(`Background ${normalizedMethod} ${endpoint} paused: no user activity (${reason})`);
    this.name = 'BackgroundRequestPausedError';
    this.code = 'BACKGROUND_REQUEST_PAUSED';
    this.endpoint = endpoint;
    this.method = normalizedMethod;
    this.reason = reason;
    this.status = undefined;
  }
}

/** How deep a `cause` chain is followed — a real wrapper nests once or twice; a cycle stops here. */
const MAX_CAUSE_DEPTH = 8;

/**
 * Is `err` a background request the session layer paused? The one predicate every poller uses to
 * treat the rejection as "skip this tick".
 *
 * It follows the ES2022 `cause` chain: a poller that re-throws its client's rejection as
 * `new Error(message, { cause })` (the console's status hooks do, to localise the message) is still
 * reporting a pause, and must not turn a skipped tick into an error on screen.
 *
 * @param {unknown} err
 * @returns {boolean}
 */
export function isBackgroundRequestPaused(err) {
  let current = err;
  for (let depth = 0; depth < MAX_CAUSE_DEPTH && current; depth += 1) {
    if (current instanceof BackgroundRequestPausedError) return true;
    current = current.cause;
  }
  return false;
}
